/**
 * Setting up configuration constants
 */

// Values we need to parse the google docs
export const sheetParseConfig = {
  configHeader: '1',
  dataHeader: 'A',
  configTitles: ['config', 'notes', 'sources'],
  sourceTypes: ['notes', 'sources', 'definitions'],
  headerTypes: ['characteristic', 'type'],
  headerSymbols: {
    'percent-unformatted': /\%|percent/,
    'currency': /\$|USD|currency/,
  },
  // These are special options on the config which apply to components at
  // many levels regardless of context. For example, locked will change the
  // behavior of table and filter rendering and the types of options that can
  // be selected
  globalRules: [
    'locked',
    'graph',
    'map',
    'geomulti',
    'context',
    'map base column',
    'link column',
    'link target',
    'related columns',
  ],
  // This serves as a reverse dictionary for data types, enabling look up and
  // type assignment by alias
  dataTypes: {
    'text': [
      'urban area',
      'name',
      'zip code',
      'city',
      'county',
      'msa',
    ],
    'year': [
      'year',
    ],
    'percent': [
      '% of us total',
      '% of fpl',
      'percent change',
      '% change',
      'mlr',
      'ratio',
    ],
    'currency': [
      'income',
    ],
    'number': [
      'rate',
      'net change',
      'rank',
    ],
    'date-format': [
      'mm/dd/yyyy',
      'mon dd, yyyy',
      'month yyyy',
      'month dd yyyy',
    ],
    'fiscal year': null,
    'default': null,
    'geography': [
      'state',
    ],
    'yes/no': null,
  },
};

// Defaults and special case configuration for Datacenter app
export const appDefault = {
  distributionName: 'Characteristics',
  hideMap: false,
  hideGraph: false,
  isLocked: false,
  // Values which can be selected on their own as rows, and should be given
  // checkboxes outside the fieldgroup list
  specialWrapups: [
    'United States',
    'World',
    'All Urban Counties',
    'All Rural Counties',
    'All MSAs',
  ],
  // The values which are used to represent N/A, and the order in which
  // they should appear when in the same collection
  dataNotAvailable: [
    '*',
    '**',
    'NSD',
    '(NSD)',
    'NR',
    'NA',
    '(NA)',
    'N/A',
    '-',
    '--',
    '',
  ],
  // Territories, for categorization when sorting
  kffTerritories: [
    'Puerto Rico',
    'Virgin Islands',
    'Guam',
    'Northern Mariana Islands',
    'American Samoa',
    'Residence Unknown',
    'Federated States of Micronesia',
    'Marshall Islands',
    'Republic of Palau',
    'U.S. Virgin Islands',
  ],
  countyOrder: [
    'states',
    'counties',
    'msas',
    'ma-regions',
    'pdp-regions',
  ],
  wrapupOrder: [
    'united-states',
    'world',
    'all-urban-counties',
    'all-rural-counties',
    'all-msas',
    'all-ma-regions',
    'all-pdp-regions',
  ],
  normalizedKffTerritories: [
    'puerto-rico',
    'virgin-islands',
    'guam',
    'northern-mariana-islands',
    'american-samoa',
    'residence-unknown',
    'federated-states-of-micronesia',
    'marshall-islands',
    'republic-of-palau',
    'us-virgin-islands',
  ],
  // Tests for a few different common row types
  isSpecialGeo: /^MA\sRegion|PDP\sRegion/,
  isStateCategory: /^All\s/,
  isSummaryRow: /[\s+]?\(All\)[\s+]?$/,
  decimalPlacesMap: {
    10000: -4,
    1000: -3,
    100: -2,
    10: -1,
    1: 0,
    0.1: 1,
    0.01: 2,
    0.001: 3,
    0.0001: 4,
    0.00001: 5,
    0.000001: 6,
    0.0000001: 7,
  },
  // CSS for padding Table Header Cells
  // We need to take this into account when wrapping header text
  // right and left padding: 20 and 10, respectively
  //
  headerPadding: 30,

  // CSS for padding Table Cells
  // We need to take this into account when wrapping cell text
  // right and left padding: 10 and 10, respectively
  //
  cellPadding: 20,

  // Max number of natural breaks for map
  maxBreaks: 4,

  // For known data types, we want to sort descending first.
  descSortFirst: ['number', 'percent', 'currency', 'year'],

  defaultWidths: {
    'text': {
      'default': 225,
      'narrow': 175,
      'wide': 300,
    },
    'year': {
      'default': 80,
      'narrow': 60,
      'wide': 140,
    },
    'percent': {
      'default': 100,
      'narrow': 75,
      'wide': 150,
    },
    'currency': {
      'default': 140,
      'narrow': 100,
      'wide': 180,
    },
    'number': {
      'default': 140,
      'narrow': 100,
      'wide': 180,
    },
    'date-format': {
      'default': 100,
      'narrow': 75,
      'wide': 150,
    },
    'fiscal year': {
      'default': 100,
      'narrow': 75,
      'wide': 150,
    },
    'default': {
      'default': 125,
    },
    'geography': {
      'default': 160,
    },
    'yes/no': {
      'default': 70,
      'narrow': 50,
      'wide': 125,
    },
  },

  // State Lookup For SHF Landing Page
  stateLookup: {
    'AL': 'Alabama',
    'AK': 'Alaska',
    'AS': 'American Samoa',
    'AZ': 'Arizona',
    'AR': 'Arkansas',
    'CA': 'California',
    'CO': 'Colorado',
    'CT': 'Connecticut',
    'DE': 'Delaware',
    'DC': 'District Of Columbia',
    'FL': 'Florida',
    'FM': 'Federated States of Micronesia',
    'GA': 'Georgia',
    'GU': 'Guam',
    'HI': 'Hawaii',
    'ID': 'Idaho',
    'IL': 'Illinois',
    'IN': 'Indiana',
    'IA': 'Iowa',
    'KS': 'Kansas',
    'KY': 'Kentucky',
    'LA': 'Louisiana',
    'ME': 'Maine',
    'MH': 'Marshall Islands',
    'MD': 'Maryland',
    'MA': 'Massachusetts',
    'MI': 'Michigan',
    'MN': 'Minnesota',
    'MS': 'Mississippi',
    'MO': 'Missouri',
    'MT': 'Montana',
    'NE': 'Nebraska',
    'NV': 'Nevada',
    'NH': 'New Hampshire',
    'NJ': 'New Jersey',
    'NM': 'New Mexico',
    'NY': 'New York',
    'NC': 'North Carolina',
    'ND': 'North Dakota',
    'MP': 'Northern Mariana Islands',
    'OH': 'Ohio',
    'OK': 'Oklahoma',
    'OR': 'Oregon',
    'PA': 'Pennsylvania',
    'PR': 'Puerto Rico',
    'PW': 'Republic of Palau',
    'RI': 'Rhode Island',
    'SC': 'South Carolina',
    'SD': 'South Dakota',
    'TN': 'Tennessee',
    'TX': 'Texas',
    'VI': 'U.S. Virgin Islands',
    'UT': 'Utah',
    'VT': 'Vermont',
    'VA': 'Virginia',
    'WA': 'Washington',
    'WV': 'West Virginia',
    'WI': 'Wisconsin',
    'WY': 'Wyoming',
  },

  // GeoMulti symbol lookup for footnotes
  footnoteSymbols: [
    '*',
    '†',
    '‡',
    'θ',
    '¶',
    'χ',
    'Δ',
    'Ψ',
    'β',
    'Ω',
  ],
};



// WEBPACK FOOTER //
// ../~/eslint-loader!./js/config/config.js